import React from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { ViewUserDialogProps } from '../types';

export function ViewUserDialog({
  isDark,
  isOpen,
  onOpenChange,
  user,
  isLoading,
  formatDate,
}: ViewUserDialogProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className={cn(
        'max-w-4xl max-h-[90vh] overflow-y-auto',
        isDark ? 'bg-[#1E293B] border-slate-700' : 'bg-white'
      )}>
        <DialogHeader>
          <DialogTitle className={cn(
            isDark ? 'text-white' : 'text-[#0F172A]'
          )}>User Details</DialogTitle>
          <DialogDescription className={cn(
            isDark ? 'text-slate-400' : 'text-slate-500'
          )}>
            Complete user information and limits
          </DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#0EA5A4]" />
          </div>
        ) : user ? (
          <div className="space-y-6 py-4">
            {/* Basic Information */}
            <UserDetailsSection title="Basic Information" isDark={isDark}>
              <DetailItem label="User ID" isDark={isDark} mono breakAll>
                {user.id}
              </DetailItem>
              <DetailItem label="Username" isDark={isDark}>
                {user.username}
              </DetailItem>
              <DetailItem label="Email" isDark={isDark}>
                {user.email || '—'}
              </DetailItem>
              <DetailItem label="Role" isDark={isDark}>
                <Badge className={cn(
                  'rounded-lg font-medium border-0',
                  user.role === 'admin'
                    ? 'bg-purple-500/10 text-purple-500'
                    : 'bg-blue-500/10 text-blue-500'
                )}>
                  {user.role}
                </Badge>
              </DetailItem>
              <DetailItem label="First Name" isDark={isDark}>
                {user.firstName || '—'}
              </DetailItem>
              <DetailItem label="Last Name" isDark={isDark}>
                {user.lastName || '—'}
              </DetailItem>
            </UserDetailsSection>

            {/* Status Information */}
            <UserDetailsSection title="Status" isDark={isDark}>
              <DetailItem label="Subscription" isDark={isDark}>
                <Badge className={cn(
                  'rounded-lg font-medium border-0',
                  user.isSubscribed
                    ? 'bg-emerald-500/10 text-emerald-500'
                    : 'bg-slate-500/10 text-slate-500'
                )}>
                  {user.isSubscribed ? 'Subscribed' : 'Free'}
                </Badge>
              </DetailItem>
              <DetailItem label="Account Type" isDark={isDark}>
                <Badge className={cn(
                  'rounded-lg font-medium border-0',
                  user.is_anonymous
                    ? 'bg-amber-500/10 text-amber-500'
                    : 'bg-blue-500/10 text-blue-500'
                )}>
                  {user.is_anonymous ? 'Anonymous' : 'Registered'}
                </Badge>
              </DetailItem>
              <DetailItem label="Subscription End" isDark={isDark}>
                {formatDate(user.subscribedEnd)}
              </DetailItem>
              <DetailItem label="Last Login" isDark={isDark}>
                {formatDate(user.lastLogin)}
              </DetailItem>
            </UserDetailsSection>

            {/* Goal & Deadline */}
            <UserDetailsSection title="Goal & Deadline" isDark={isDark}>
              <DetailItem label="Goal" isDark={isDark}>
                {user.goal ? (
                  <Badge className={cn(
                    'rounded-lg font-medium border-0',
                    user.goal === 'first_exam'
                      ? 'bg-blue-500/10 text-blue-500'
                      : user.goal === 'retake'
                        ? 'bg-amber-500/10 text-amber-500'
                        : 'bg-purple-500/10 text-purple-500'
                  )}>
                    {user.goal === 'first_exam' ? 'First Exam' : 
                     user.goal === 'retake' ? 'Retake' : 'Repeat'}
                  </Badge>
                ) : '—'}
              </DetailItem>
              <DetailItem label="Deadline" isDark={isDark}>
                {user.deadline ? (
                  <Badge className={cn(
                    'rounded-lg font-medium border-0',
                    user.deadline === '3_days'
                      ? 'bg-red-500/10 text-red-500'
                      : user.deadline === '1_week'
                        ? 'bg-amber-500/10 text-amber-500'
                        : 'bg-emerald-500/10 text-emerald-500'
                  )}>
                    {user.deadline === '3_days' ? '3 Days' : 
                     user.deadline === '1_week' ? '1 Week' : '1 Month'}
                  </Badge>
                ) : '—'}
              </DetailItem>
            </UserDetailsSection>

            {/* Device & OAuth IDs */}
            <UserDetailsSection title="Device & OAuth IDs" isDark={isDark} cols={3}>
              <DetailItem label="Device ID" isDark={isDark} mono breakAll>
                {user.deviceId || '—'}
              </DetailItem>
              <DetailItem label="Google ID" isDark={isDark} mono breakAll>
                {user.googleId || '—'}
              </DetailItem>
              <DetailItem label="Apple ID" isDark={isDark} mono breakAll>
                {user.appleId || '—'}
              </DetailItem>
            </UserDetailsSection>

            {/* Limits Information */}
            {user.limits && (
              <UserDetailsSection title="Usage Limits" isDark={isDark}>
                <DetailItem label="Mock Exam Attempts" isDark={isDark}>
                  <LimitDisplay 
                    used={user.limits.mock_exam_attempts_used}
                    remaining={user.limits.mock_exam_attempts_remaining}
                    isDark={isDark}
                  />
                </DetailItem>
                <DetailItem label="Wrong Exam Attempts" isDark={isDark}>
                  <LimitDisplay 
                    used={user.limits.wrong_exam_attempts_used}
                    remaining={user.limits.wrong_exam_attempts_remaining}
                    isDark={isDark}
                  />
                </DetailItem>
                <DetailItem label="Marathon Attempts" isDark={isDark}>
                  <LimitDisplay 
                    used={user.limits.marathon_attempts_used}
                    remaining={user.limits.marathon_attempts_remaining}
                    isDark={isDark}
                  />
                </DetailItem>
                <DetailItem label="Premium Status" isDark={isDark}>
                  <Badge className={cn(
                    'rounded-lg font-medium border-0',
                    user.limits.isPremium
                      ? 'bg-emerald-500/10 text-emerald-500'
                      : 'bg-slate-500/10 text-slate-500'
                  )}>
                    {user.limits.isPremium ? 'Premium' : 'Free'}
                  </Badge>
                </DetailItem>
              </UserDetailsSection>
            )}

            {/* Dates */}
            <UserDetailsSection title="Dates" isDark={isDark}>
              <DetailItem label="Created At" isDark={isDark}>
                {formatDate(user.created_at)}
              </DetailItem>
              <DetailItem label="Updated At" isDark={isDark}>
                {formatDate(user.updated_at)}
              </DetailItem>
            </UserDetailsSection>
          </div>
        ) : null}
        <DialogFooter>
          <Button
            onClick={() => onOpenChange(false)}
            className="bg-[#0EA5A4] hover:bg-[#0EA5A4]/90"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Helper Components
interface UserDetailsSectionProps {
  title: string;
  isDark: boolean;
  cols?: 2 | 3;
  children: React.ReactNode;
}

function UserDetailsSection({ title, isDark, cols = 2, children }: UserDetailsSectionProps) {
  return (
    <div>
      <h3 className={cn(
        'text-lg font-semibold mb-4',
        isDark ? 'text-white' : 'text-[#0F172A]'
      )}>{title}</h3>
      <div className={cn(
        'grid gap-4',
        cols === 3 ? 'grid-cols-3' : 'grid-cols-2'
      )}>
        {children}
      </div>
    </div>
  );
}

interface DetailItemProps {
  label: string;
  isDark: boolean;
  mono?: boolean;
  breakAll?: boolean;
  children: React.ReactNode;
}

function DetailItem({ label, isDark, mono, breakAll, children }: DetailItemProps) {
  const isTextContent = typeof children === 'string' || typeof children === 'number';
  
  return (
    <div className="space-y-1">
      <p className={cn(
        'text-xs font-medium',
        isDark ? 'text-slate-400' : 'text-slate-500'
      )}>{label}</p>
      {isTextContent ? (
        <p className={cn(
          'text-sm',
          isDark ? 'text-slate-300' : 'text-slate-700',
          mono && 'font-mono',
          breakAll && 'break-all'
        )}>{children}</p>
      ) : children}
    </div>
  );
}

interface LimitDisplayProps {
  used: number;
  remaining: number;
  isDark: boolean;
}

function LimitDisplay({ used, remaining, isDark }: LimitDisplayProps) {
  return (
    <p className={cn(
      'text-sm',
      isDark ? 'text-slate-300' : 'text-slate-700'
    )}>
      Used: {used}
      {remaining >= 0 && (
        <span className={cn(
          'ml-2',
          isDark ? 'text-slate-400' : 'text-slate-500'
        )}>
          (Remaining: {remaining})
        </span>
      )}
      {remaining === -1 && (
        <span className="ml-2 text-emerald-500">Unlimited</span>
      )}
    </p>
  );
}
